export interface Post {
    id: number;
    id_user: number;
    type: string;
    original_post_id: number;
    is_hidden?: boolean;
    hidden_reason?: string;
    group_id: number | null;
    total_likes: number;
    total_shares: number;
    total_comments: number;
    text: string;
    liked: boolean;
    user_is_blocked: boolean;
    created_at: string;
    updated_at: string;
    content: string;
    user: User;
    images: ImagePost[]
    original_post: Post;
    poll: Poll;
    gif_url?: string;
    group: Group
    ads: Ads
    reported?: boolean;
}

export interface Ads {
    id: number;
    post_id: number;
    name: string;
    status: "pendente" | "ativo" | "validando_conteudo" | "online" | "off" | "reprovado"
    post: Post;
    button_text: string;
    button_url: string;
    price: string | number | null;
}

export interface AdsPreview {
    user: User | null;
    text: string;
    buttonText: string;
    buttonUrl: string;
    price: string | number | null;
    content: string;
    images?: ImagePost[]
}

export interface Pix {
    chave: string;
    pixCopiaECola: string;
    revisao: number;
    status: "ATIVA" | "pago";
    txid: string;
    valor: { original: string };
}

export interface Payment {
    status: "ATIVA" | "pago" | "pendente";
    pixCopiaECola: string;
    txid: string;
    valor: string;
    qrcode:  string;
}

const PAYMENTS = {
  GERENCIANET_PIX: "gerencianet_pix",
  MERCADOPAGO_CARD: "mercadopago_card",
  CODE: "code",
  WALLET: "wallet",
} as const;
export type PaymentTypes = typeof PAYMENTS[keyof typeof PAYMENTS];

export interface QrCode {
    imagemQrcode: string;
    linkVisualizacao: string;
    qrcode: string;
}

export interface Community{
    id: number;
    img: string;
    description: string;
    color: string;
    can_edit: boolean;
    can_manage_members: boolean;
    is_admin: boolean;
    can_delete: boolean;
    name: string;
    total_members: number;
    total_posts: number;
}

export interface Comment{
    id: number;
    id_comment: number;
    responses: any;
    total_responses: number;
    total_likes: number;
    is_liked: boolean;
    can_edit: boolean;
    can_delete: boolean;
    can_pin?: boolean;
    is_pinned?: boolean;
    text: string;
    textFormatted: string;
    user: User;
    original_comment: Comment;
}

export interface User {
    sexo?: string;
    subscription_active?: boolean;
    subscription_months?: number;
    has_stories?: boolean;
    referral_code?: string;
    relationship?: string;
    pronoun?: string;
    session_id: string;
    country?: string;
    readbook_user_id?: number | null;
    custom_fields?: IUserCustomField[];
    created_at: Date;
    next_payment: Date;
    birth_date: Date;
    phone: string;
    email: string;
    can_ban_user: any;
    is_admin?: boolean;
    pinned_post_id: number;
    verified_profile: number;
    privacy: number;
    is_following: boolean;
    is_followed: boolean;
    can_edit_permissions: boolean;
    can_see_location?: boolean;
    location?: UserLocation;
    is_blocked: boolean;
    is_muted: boolean;
    permissions?: string[];
    site: string;
    token: string;
    is_banned: boolean | Date | null;
    biography: string;
    is_visible: boolean;
    can_remove_photo: boolean;
    can_send_warn: boolean;
    can_delete_account: boolean;
    can_manager_readbooks_account: boolean; //pode vincular e desvincular conta do readbooks
    email_verified_at: Date;
    position: number;
    user_id: any;
    google2fa_enabled?: boolean;
    id: number;
    name: string;
    user_name: string;
    img_account: string;
    img_banner?: string;
    total_posts: number;
    total_videos_youtube: number;
    total_poems: number;
    total_followers: number;
    total_following: number;
    total_books_publish: number;
    total_comments: number;
    reported?: boolean;
    nuestra_uuid?: string;
    active_frame?: ActiveFrame
    is_online?: boolean;
    is_company?: boolean;
    show_online?: ViewProfilePrivacy;
    weekly_rank?: number;
    weekly_points?: number;
    weekly_position?: number;
    streak?: { current: 0, best: 0 };
    is_verified_company?: boolean;
    company?: Company;
    stamps: Stamp[]
}

interface Company {
    corporate_reason: string;
    business_name: string;
    cnpj: string;
    website: string;
    email: string;
    responsible_document?: File;
    social_contract?: File;
}

export type ViewProfilePrivacy = | "all" | "following" | "none";

export type ProfileFieldKey =
  | "music"
  | "movies"
  | "languages"
  | "pets"
  | "websites"
  | "hobbies"
  | "sports"
  | "activities"
  | "studies"
  | "games"
  | "children"
  | "drinks"
  | "foods"
  | "achievements"
  | "jobs"
  | "passions"
  | "places"
  | "interests"
  | "skills"
  | "recommendations";

export interface IUserCustomField {
  key: ProfileFieldKey;
  privacy: "public" | "friends" | "private";
  values: string[];
}


export interface ActiveFrame{
    image_path: string
}

export interface PollVotes {
    id: number,
    poll_id: number,
    poll_option_id: number,
    user_id: number,
    created_at: Date,
    updated_at: Date
}
export interface PollOption {
    id: number,
    poll_id: number,
    option_text: string,
    votes_count: number,
    created_at: Date,
    updated_at: Date,
    votes: PollVotes[],
    user_vote: any
}

export interface Poll {
    id: number, 
    post_id: number, 
    question: string, 
    multiple_choice: boolean,
    options: PollOption[]
    updated_at: Date
    created_at: Date
}


export interface PollProps {
    poll: Poll;
    postId: number;
}

const STAMPS = {
  YELLOW: "yellow",
  BLACK: "black",
  GRAY: "gray",
  BLUE: "blue",
  AUTISM: "autism",
  LGBTQ: "lgbt",
  PURPLE: "purple",
  BROWN: "brown",
  DARK_BLUE: "darkBlue",
  RED: "red",
  PEACH: "peach",
  HIDDEN_DEFICIENCIES: "hidden_deficiencies",
} as const;
export type Stamp = typeof STAMPS[keyof typeof STAMPS];

export interface UserLocation {
    ip_address: string;
    country: string;
    region_name: string;
    city: string;
}

export interface Book {
    id_livro: number
    uuid: string;
    total_paginas: number
    paginas_lidas: number
    img_livro: string
    nome_livro: string
    descricao_livro: string
    tempo_lido: string
    data_inicio: string
    data_termino: string
    lido: string
    title: string
}

export interface Playlist {
    id: number
    playlist_id: number
    thumbnail: string;
    title: string;
}

export interface VideoPlaylist {
    id: number
    video_id: number
    thumbnail: string;
    title: string;
}

export interface Group {
    id: number;
    img: any;
    name: number;
    description: string;
}

export interface ImagePost {
    id: number;
    path: string;
    type: number;
    id_post: number;
    file?: File,
}

export interface Ban{
    id: number;
    blocked_until: Date;
    created_at: Date;
    user_id: boolean;
    user: User;
}

export interface Notification{
    id: number;
    opened: boolean;
    text: string;
    link1: string;
    img_notification: string;
    title: string;
    type: string;
    created_at: Date;
} 

export interface Chat{
    chat_id: number;
    img_account: string;
    name: string;
    user_name: string;
    user_id: string;
}

export interface MessageChat{
    id: number;
    chat_id: number;
    created_at: string;
    text: string;
    user_id: number;
    isTyping?: boolean;
    idNotSend?: boolean;
    errorSend?: boolean;
}

export interface Session{
    id: number;
    browser: string;
    platform: string;
    updated_at: string;
    last_activity: string;
    user_agent: string;
    ip_address: string;
    country: string;
    city: string;
    region_name: string;
    token: string;
}

export interface Poem {
  id: number;
  uuid: string;
  title: string;
  text: string;
  user: User;
  created_at: string;
  category: string;
  author: string;
  images: { id:number;path: string; type: string, bucket: "pacoca" | "reflexo" }[];
}

export interface DailyStreak {
  id: number;
  user_id: number;
  current_day: number;
  last_claimed_at: Date;
}


export enum System {
    pacoca = "Paçoca Oficial",
    echo = "Echo",
    reflexo = "Reflexo",
    readbooks = "Read Books",
    rita = "Rita",
    nuestra = "Nuestra",
    cronos = "Cronos",
    versona = "Versona",
    translate = "Paçoca Translate"
}